import { useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { getBase64 } from "../../util/Base64";
import { save } from "./api-aluno-foto";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import MuralFotoLightbox from "../Mural/MuralFoto/MuralFotoLightbox";
import AlunoFotoList from "./AlunoFotoList";

const MAX_FILE_SIZE = 10000000;

const AlunoFotoInsert = () => {
  const navigate = useNavigate();
  const hiddenFileInput = useRef(null);
  const [anoLetivo, setAnoLetivo] = useState(String(new Date().getFullYear()));
  const [listaAF, setListaAF] = useState([]);
  const [progresso, setProgresso] = useState(null);
  const [falhas, setFalhas] = useState([]);
  const [fotoIndex, setFotoIndex] = useState(null);

  const anoValido = /^\d{4}$/.test(anoLetivo);
  const comFoto = listaAF.filter((af) => af.foto);
  // A grade usa a posição na lista completa; o visualizador só recebe fotos com imagem.
  const handleOpenFoto = (index) => setFotoIndex(comFoto.indexOf(listaAF[index]));
  const isUploading = progresso !== null;

  const handleChangeImage = async (event) => {
    const files = Array.from(event.target.files || []);
    // Limpa o input para permitir enviar os mesmos arquivos novamente.
    event.target.value = "";
    if (files.length === 0) return;

    const novasFalhas = files
      .filter((file) => file.size > MAX_FILE_SIZE)
      .map((file) => ({ nome: file.name, motivo: "maior que 10Mb" }));
    const validos = files.filter((file) => file.size <= MAX_FILE_SIZE);

    let enviadas = 0;
    // Envia uma de cada vez para não sobrecarregar o servidor com imagens em base64.
    for (let i = 0; i < validos.length; i++) {
      const file = validos[i];
      setProgresso({ atual: i + 1, total: validos.length });
      const foto = await getBase64(file);
      const data = await save({
        foto,
        anoLetivo,
        fileType: file.type,
        fileName: file.name,
        fileSize: file.size,
      });
      if (!data || data.error) {
        novasFalhas.push({ nome: file.name, motivo: data?.message || "erro ao enviar" });
        continue;
      }
      enviadas += 1;
      setListaAF((prevState) => [data, ...prevState]);
    }

    setProgresso(null);
    setFalhas(novasFalhas);
    if (enviadas > 0) success(`${enviadas} foto(s) enviada(s).`);
  };

  return (
    <Container>
      <MuralFotoLightbox
        fotos={comFoto}
        index={fotoIndex}
        onClose={() => setFotoIndex(null)}
        onChangeIndex={setFotoIndex}
      />

      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Tooltip title="Voltar">
          <IconButton edge="start" onClick={() => navigate("/aluno-foto")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6">Inserir Fotos por Ano Letivo</Typography>
          <Typography variant="body2" color="text.secondary">
            Envie várias fotos de uma vez para o ano letivo informado.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
          <TextField
            size="small"
            label="Ano Letivo"
            value={anoLetivo}
            disabled={isUploading}
            onChange={(event) => setAnoLetivo(event.target.value.replace(/\D/g, "").slice(0, 4))}
            error={!anoValido}
            helperText={anoValido ? " " : "Informe o ano com 4 dígitos."}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
          <input
            type="file"
            accept=".jpg,.jpeg,.png"
            multiple
            hidden
            ref={hiddenFileInput}
            onChange={handleChangeImage}
          />
          <Button
            variant="contained"
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            disabled={!anoValido || isUploading}
            onClick={() => hiddenFileInput.current.click()}
          >
            Selecionar Fotos
          </Button>
        </Stack>

        {isUploading && (
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Enviando {progresso.atual} de {progresso.total}...
            </Typography>
            <LinearProgress variant="determinate" value={(progresso.atual / progresso.total) * 100} />
          </Box>
        )}

        {falhas.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setFalhas([])}>
            {falhas.length} arquivo(s) não enviado(s):
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {falhas.map((f) => (
                <li key={f.nome}>
                  {f.nome} — {f.motivo}
                </li>
              ))}
            </Box>
          </Alert>
        )}
      </Paper>

      {listaAF.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
            Enviadas nesta sessão ({listaAF.length})
          </Typography>
          <AlunoFotoList listaAF={listaAF} showAluno onOpenFoto={handleOpenFoto} />
        </>
      )}
    </Container>
  );
};

export default AlunoFotoInsert;
